import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Per-view scroll-disable state.
 *
 * Some screens (the itinerary content view, the team-member overlay) manage their
 * own inner scroller and need the surrounding MobileAppFrame scroll switched off.
 *
 * Under iOS native navigation, the app's whole React tree is portaled into every
 * native view at once, so all tabs mount simultaneously and share one React tree.
 * A single global flag would let one tab disable scrolling in every other tab.
 * Scoping the flag to a provider rendered once per view (inside the portaled
 * subtree) keeps each view's MobileAppFrame independent. On web / Android there is
 * only one screen and one provider instance, so this behaves the same as a global flag.
 */
interface ScrollDisabledValue {
  disabled: boolean;
  setDisabled: (disabled: boolean) => void;
}

const ScrollDisabledContext = createContext<ScrollDisabledValue | null>(null);

export function ScrollDisabledProvider({ children }: { children: ReactNode }) {
  const [disabled, setDisabled] = useState(false);
  const value = useMemo(() => ({ disabled, setDisabled }), [disabled]);
  return <ScrollDisabledContext.Provider value={value}>{children}</ScrollDisabledContext.Provider>;
}

/**
 * Read/set the surrounding view's scroll-disable state. Falls back to a no-op when
 * used outside a provider (tests, storybook) so consumers never crash.
 */
export function useScrollDisabled(): ScrollDisabledValue {
  const ctx = useContext(ScrollDisabledContext);
  if (!ctx) {
    return { disabled: false, setDisabled: () => {} };
  }
  return ctx;
}
